import clsx from 'clsx';
import React, { useId } from 'react';

import OfferCards from '@components/pages/SubscribeTest/OfferSelection/OfferCards';
import OfferTable from '@components/pages/SubscribeTest/OfferSelection/OfferTable';
import useModifiedBenefits from '@hooks/useModifiedBenefits';
import useWindowSize from '@hooks/useWindowSize';
import { subscribe_one_path } from '@routes';
import type { OfferOption, SelectedOffer } from '@utils/backend-api/subscribe';
import { WIDTH_SM } from '@utils/constants';

import subscribeParams from '../utils/subscribeParams';
import SubscribeMatrixCustom from './SubscribeMatrixCustom';
import SubscribeMatrixTwoColumn from './SubscribeMatrixTwoColumn';
import { marginValues, paddingValues } from './constants';
import type { SubscribeMatrixBlockProps } from './interfaces';

const SubscribeMatrixBlock = ({ config, offers, redirectTo }: SubscribeMatrixBlockProps) => {
  const cardIdPrefix = `matrix-${useId().replace(/:/g, '')}-`;
  const isCustom = config?.template_variant === 'custom';
  const isTwoColumn = config?.template_variant === 'two_column';
  const monthlyOffer = config?.show_monthly ? offers?.monthly_offer : undefined;
  const annualOffer = config?.show_annual ? offers?.annual_offer : undefined;
  const proOffer = config?.show_pro ? offers?.pro_offer : undefined;
  const ypOffer = offers?.yp_offer;
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth < WIDTH_SM;

  const modifiedBenefits = useModifiedBenefits(monthlyOffer, annualOffer, proOffer);
  const filteredOffersToDisplay = [monthlyOffer, annualOffer, proOffer].filter(
    (offer): offer is OfferOption => Boolean(offer)
  );

  const handleOfferClick = (plan: SelectedOffer) => {
    if (!plan?.code) return;

    const anchor = document.createElement('a');
    anchor.href = subscribe_one_path(subscribeParams({ offerCode: plan?.code, redirectTo }));
    anchor.click();
  };

  return (
    <div
      className={clsx(
        'subscribe-matrix-block my-0',
        config?.css_classes,
        config?.margin && marginValues[config.margin],
        config?.padding && paddingValues[config.padding]
      )}
    >
      {isCustom ? (
        <SubscribeMatrixCustom config={config} offers={offers} redirectTo={redirectTo} />
      ) : (
        <>
          {isTwoColumn && !isMobile && (
            <SubscribeMatrixTwoColumn
              annualOffer={annualOffer}
              proOffer={proOffer}
              redirectTo={redirectTo}
            />
          )}
          {(!isTwoColumn || (isMobile && isTwoColumn)) && (
            <OfferTable
              benefits={modifiedBenefits}
              offers={filteredOffersToDisplay}
              onOfferClick={() => {}}
              setSelectedOffer={handleOfferClick}
            />
          )}
        </>
      )}
      <OfferCards
        idPrefix={cardIdPrefix}
        onOfferClick={() => {}}
        setSelectedOffer={handleOfferClick}
        showGroup={config?.show_group}
        showYP={Boolean((config?.show_yp ?? true) && ypOffer)}
        ypOffer={ypOffer ?? undefined}
      />
    </div>
  );
};

export default SubscribeMatrixBlock;
